import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  clearAdminSession,
  getAdminSession,
  setAdminSession,
  subscribeAdminSession,
} from '../../shared/api/adminSessionStore';
import {
  exchangeAdminClerkSession,
  fetchAdminWebSession,
} from '../../shared/api/clerkAuthApi';
import {
  buildAdminCookieSession,
  isAuthenticatedWebSession,
} from '../../shared/api/browserSession';
import { useClerkRuntime } from './ClerkRuntimeProvider';
import { ENABLE_AUTH } from '../../shared/utils/constants';

const AdminSessionContext = createContext({
  session: null,
  isReady: true,
  setSession: () => {},
  clearSession: () => {},
});

function isAdminPath(pathname = '') {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

export function useAdminSession() {
  return useContext(AdminSessionContext);
}

export default function AdminSessionProvider({ children }) {
  const location = useLocation();
  const [session, setSessionState] = useState(() => getAdminSession());
  const [isReady, setIsReady] = useState(() => {
    if (!ENABLE_AUTH) {
      return true;
    }

    const initialSession = getAdminSession();
    if (initialSession?.accessToken) {
      return true;
    }

    return typeof window !== 'undefined' ? !isAdminPath(window.location.pathname) : true;
  });
  const bootstrapAttemptRef = useRef('');
  const clerkRuntime = useClerkRuntime();
  const withinAdmin = isAdminPath(location.pathname);

  useEffect(() => {
    const unsubscribe = subscribeAdminSession((nextSession) => {
      setSessionState(nextSession);
      if (nextSession?.accessToken) {
        setIsReady(true);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (
      clerkRuntime.adminEnabled &&
      clerkRuntime.isLoaded &&
      !clerkRuntime.isSignedIn &&
      session?.authSource === 'clerk'
    ) {
      clearAdminSession();
    }
  }, [
    clerkRuntime.adminEnabled,
    clerkRuntime.isLoaded,
    clerkRuntime.isSignedIn,
    session?.authSource,
  ]);

  useEffect(() => {
    if (!ENABLE_AUTH || !withinAdmin) {
      setIsReady(true);
      return undefined;
    }

    if (session?.accessToken) {
      setIsReady(true);
      return undefined;
    }

    const attemptKey = [
      location.pathname,
      String(clerkRuntime.user?.id || ''),
      String(clerkRuntime.isSignedIn),
      String(clerkRuntime.isLoaded),
    ].join(':');
    if (bootstrapAttemptRef.current === attemptKey) {
      return undefined;
    }

    bootstrapAttemptRef.current = attemptKey;
    setIsReady(false);
    let mounted = true;
    const currentSession = getAdminSession() || {};

    Promise.resolve()
      .then(() => fetchAdminWebSession())
      .then((result) => {
        if (!mounted) {
          return null;
        }

        if (isAuthenticatedWebSession(result)) {
          setAdminSession(buildAdminCookieSession(result, currentSession));
          setIsReady(true);
          return null;
        }

        if (!clerkRuntime.adminEnabled) {
          clearAdminSession();
          setIsReady(true);
          return null;
        }

        if (!clerkRuntime.isLoaded) {
          return null;
        }

        if (!clerkRuntime.isSignedIn) {
          clearAdminSession();
          setIsReady(true);
          return null;
        }

        return clerkRuntime
          .getToken()
          .then((clerkToken) => {
            if (!mounted || !clerkToken) {
              return null;
            }

            return exchangeAdminClerkSession({ clerkToken });
          })
          .then((exchangeResult) => {
            if (!mounted || !isAuthenticatedWebSession(exchangeResult)) {
              clearAdminSession();
              setIsReady(true);
              return;
            }

            setAdminSession(buildAdminCookieSession(exchangeResult, currentSession, 'clerk'));
            setIsReady(true);
          });
      })
      .catch(() => {
        if (mounted) {
          clearAdminSession();
          setIsReady(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [clerkRuntime, location.pathname, session?.accessToken, withinAdmin]);

  const value = useMemo(
    () => ({
      session,
      isReady,
      setSession: (nextSession) => setAdminSession(nextSession),
      clearSession: () => clearAdminSession(),
    }),
    [isReady, session]
  );

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
}
